/* eslint-disable react-hooks/rules-of-hooks */
import { SimpleOption } from "@/components/governance/search/AmbientFacilitiesContext";
import { EncryptMappingStatus, FieldMappingResponseDto, PrimaryClassification, SecondaryClassificationResponseDto } from "@/gen/api/governance";
import { ZUtils } from "./FieldsMappingList.col.validate";
import { FormControl, Select, MenuItem, FormHelperText, Box, Typography, SelectChangeEvent } from "@mui/material";
import { EncryptionRuleResponseDto } from "@/gen/api/encrypt";
import { SelectedEncryptor } from "./FieldsMappingList.hooks";
import { RecPatch } from "@/dataProvider/tools";
import { ListControllerProps, RecordMap } from "ra-core";
import { FieldMappingCatagory } from "@/pages/obfuscation/mutateEncryptionMappingsController";
import { useEventBus } from "@/contexts/EventBusContext/useEventBus";
import { adjustErrorHelperText, couplingValidationRequest, E_FieldMapping, emsChangePayload, ET_FieldMapping } from "./FieldsMappingList.events";


interface EncryptRuleColumnProps {
  fieldDto: FieldMappingResponseDto;
  utils: ZUtils['utils'],
  plainStateUtils: ZUtils['plainStateUtils'],
  hasWritePermit: boolean;
  encryptSettingChangeLocked?: boolean | undefined;
  classificationSelectOptions: SimpleOption[];
  builtInMappingRules: EncryptionRuleResponseDto[];
  builtInMappingRulesBag: RecordMap<EncryptionRuleResponseDto & RecPatch> | undefined;
  getListCtx: () => ListControllerProps<FieldMappingResponseDto & RecPatch>;
}

const EncryptRuleColumn = function comp({
  fieldDto, utils, plainStateUtils, hasWritePermit, encryptSettingChangeLocked, classificationSelectOptions, 
  builtInMappingRules, builtInMappingRulesBag, getListCtx,
}: EncryptRuleColumnProps
) {
  const { setStates, states } = utils
  const { fieldProps, commonMutate } = plainStateUtils

  const cvr = couplingValidationRequest(states)
  const { error, helperText } = adjustErrorHelperText(states, 'encryptionRuleId', fieldProps, cvr.E)

  const validRuleOptions = builtInMappingRules.filter(r => fieldDto.dataType && r.dataTypes && r.dataTypes.split(',').filter(s => s).includes(fieldDto.dataType))

  const pcls = 
  (!states || !states.stage.secondaryClassificationId) ? PrimaryClassification.Public
  : ((classificationSelectOptions.find(scls => scls.key === states.stage.secondaryClassificationId) as SecondaryClassificationResponseDto
    )?.primaryClassification || PrimaryClassification.Public)
  
  const encryptor = states && validRuleOptions.find(r => r.id === states.stage.encryptionRuleId)

  
  const selectedEncryptRule = ((): SelectedEncryptor => {
    if (fieldDto.fieldId) {
      return states?.stage.isSkipped ? 'skip'
      : ((builtInMappingRulesBag && builtInMappingRulesBag[states?.stage.encryptionRuleId || '']) 
        || '')
    }
    return fieldDto.isSkipped ? 'skip'
    : ((builtInMappingRulesBag && builtInMappingRulesBag[fieldDto.encryptionRuleId || '']) 
      || '')
  })();
  
  const eventBus = useEventBus('fields-mapping-list')

  const handleEncryptorChange = (e: SelectChangeEvent<SelectedEncryptor>) => {
    if (fieldDto.fieldId === undefined) { return }

    let toCata:FieldMappingCatagory  = 'skip'
    if (e.target.value === 'skip') {
      toCata = 'skip'
      setStates?.(v => {
        if (!commonMutate) return v;
        let v1 = {...v}
        v1.stage.isSkipped = true
        v1 = commonMutate('encryptionRuleId', null)(v1)
        v1.touched.encryptionRuleId = fieldDto.encryptMappingStatus !== EncryptMappingStatus.Skipped
        eventBus.publish(E_FieldMapping.EncryptMappingStatusChange,
          [emsChangePayload(fieldDto, v1.stage)] as ET_FieldMapping['encrypt_mapping_status_change'])
        return v1
      })
    } else {
      toCata = 'mapped'
      const encryptor = e.target.value as EncryptionRuleResponseDto
      setStates?.(v => {
        if (!commonMutate) return v;
        let v1 = {...v}
        v1.stage.isSkipped = false
        v1 = commonMutate('encryptionRuleId', encryptor.id)(v1)
        eventBus.publish(E_FieldMapping.EncryptMappingStatusChange,
          [emsChangePayload(fieldDto, v1.stage)] as ET_FieldMapping['encrypt_mapping_status_change'])
        return v1
      })
    }
    const { selectedIds, onToggleItem } = getListCtx()
    if (toCata !== 'skip' && selectedIds.some(id => id === fieldDto.fieldId)) {
      onToggleItem(fieldDto.fieldId)
    }
  }

  return (
    <>
      <FormControl error={error}>
        <Select
          disabled={!hasWritePermit || encryptSettingChangeLocked}
          value={selectedEncryptRule}
          onChange={handleEncryptorChange}
          size="small"
          sx={{ minWidth: 80 }}
        >
          <MenuItem value={"skip"}>Skip</MenuItem>
          {(pcls !== PrimaryClassification.Public)
          && validRuleOptions.map(r => (
            <MenuItem key={r.id} value={r as any}>{r.name}</MenuItem>
          ))}
        </Select>
        {error && 
          <FormHelperText id={`${fieldDto.fieldId}-encrypt-rule-helper-text`}>{helperText}</FormHelperText>}
      </FormControl>
      {encryptor &&
        <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
          <Typography variant="caption" sx={{ color: 'primary.light', '& span': { width: 44, display: 'inline-block' } }}>
            <span>Input: </span>{encryptor.sampleInput}
          </Typography>
          <Typography variant="caption" sx={{ color: 'success.main' }}>Output: {encryptor.sampleOutput}</Typography>
        </Box>}
    </>
  )
}

export default EncryptRuleColumn;

